import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSeoMeta } from '@unhead/react';
import {
  CloudSun,
  Layers,
  LocateFixed,
  MapPin,
  Moon,
  PanelRightOpen,
  Plane,
  Radar,
  Satellite,
  Sun,
  Telescope,
} from 'lucide-react';
import { LoginArea } from '@/components/auth/LoginArea';
import { SearchBox } from '@/components/SearchBox';
import { SkyMap, type FlyTarget, type LayerState } from '@/components/SkyMap';
import { SkyView } from '@/components/SkyView';
import { LocationPanel } from '@/components/panels/LocationPanel';
import { ObjectsPanel } from '@/components/panels/ObjectsPanel';
import { WeatherPanel } from '@/components/panels/WeatherPanel';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAircraft, type MapView } from '@/hooks/useAircraft';
import { useAstronomy } from '@/hooks/useAstronomy';
import { useReverseGeocode } from '@/hooks/useReverseGeocode';
import { useSatellitePositions } from '@/hooks/useSatellitePositions';
import { useTheme } from '@/hooks/useTheme';
import { useTles } from '@/hooks/useTles';
import { useToast } from '@/hooks/useToast';
import { useWeather } from '@/hooks/useWeather';
import type { Aircraft } from '@/lib/aircraft';
import { formatDD, geohash, haversineKm, type GeoPoint } from '@/lib/geo';

const NO_AIRCRAFT: Aircraft[] = [];

function ThemeToggle() {
  const { theme, setTheme } = useTheme();
  return (
    <Button
      variant="ghost"
      size="icon"
      aria-label="Toggle theme"
      title="Toggle theme"
      onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
    >
      {theme === 'dark' ? <Sun className="size-4" /> : <Moon className="size-4" />}
    </Button>
  );
}

const ALTITUDE_LEGEND: Array<[string, string]> = [
  ['#34d399', '< 3k ft'],
  ['#22d3ee', '3–10k ft'],
  ['#818cf8', '10–20k ft'],
  ['#c084fc', '20–32k ft'],
  ['#f472b6', '> 32k ft'],
  ['#94a3b8', 'Ground'],
];

const Index = () => {
  useSeoMeta({
    title: 'SkyScope — Live Aircraft, Satellites & Sky Tracker',
    description:
      'Track aircraft, satellites, and the zeniths of the Sun, Moon and planets on a live world map. Sky view, worldwide weather, and geocodes for any point on Earth.',
  });

  const [location, setLocation] = useState<GeoPoint>({ lat: 51.5, lon: 10.1 });
  const [flyTo, setFlyTo] = useState<FlyTarget | null>(null);
  const [view, setView] = useState<MapView | null>(null);
  const [layers, setLayers] = useState<LayerState>({
    planes: true,
    sats: true,
    astro: true,
    night: true,
  });
  const [tileStyle, setTileStyle] = useState<'dark' | 'osm'>('dark');
  const [selectedSat, setSelectedSat] = useState<string | null>(null);
  const [selectedPlane, setSelectedPlane] = useState<string | null>(null);
  const [panelsOpen, setPanelsOpen] = useState(false);
  const { toast } = useToast();

  const aircraftQuery = useAircraft(view, layers.planes);
  const tlesQuery = useTles(layers.sats);
  const satellites = useSatellitePositions(tlesQuery.data, location, layers.sats);
  const { bodies, nightUrl } = useAstronomy(location, tileStyle === 'dark' ? 1 : 0.55);
  const weatherQuery = useWeather(location);
  const placeQuery = useReverseGeocode(location);

  const handleLocationChange = useCallback((p: GeoPoint) => setLocation(p), []);
  const handleViewChange = useCallback((v: MapView) => setView(v), []);
  const flyToPoint = useCallback((p: GeoPoint, zoom: number) => {
    setFlyTo({ point: p, zoom, nonce: Date.now() });
  }, []);

  // One-shot geolocation on first load.
  useEffect(() => {
    if (!('geolocation' in navigator)) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const p = { lat: pos.coords.latitude, lon: pos.coords.longitude };
        setLocation(p);
        setFlyTo({ point: p, zoom: 8, nonce: Date.now() });
      },
      () => undefined,
      { timeout: 6000, maximumAge: 300_000 },
    );
  }, []);

  const locateMe = () => {
    if (!('geolocation' in navigator)) {
      toast({ title: 'Geolocation is not supported by this browser', variant: 'destructive' });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const p = { lat: pos.coords.latitude, lon: pos.coords.longitude };
        setLocation(p);
        setFlyTo({ point: p, zoom: 9, nonce: Date.now() });
      },
      () =>
        toast({
          title: 'Could not determine your location',
          description: 'Check your browser permissions.',
          variant: 'destructive',
        }),
      { timeout: 8000 },
    );
  };

  const aircraft = aircraftQuery.data?.aircraft ?? NO_AIRCRAFT;

  // Cap rendered aircraft for performance: nearest 160 to the map center (+ selection).
  const planesForMap = useMemo(() => {
    const center: GeoPoint = view ? { lat: view.lat, lon: view.lon } : location;
    const sorted = [...aircraft].sort((a, b) => haversineKm(center, a) - haversineKm(center, b));
    const top = sorted.slice(0, 160);
    if (selectedPlane && !top.some((p) => p.hex === selectedPlane)) {
      const sel = aircraft.find((p) => p.hex === selectedPlane);
      if (sel) top.push(sel);
    }
    return top;
  }, [aircraft, view, location, selectedPlane]);

  const sun = bodies.find((b) => b.name === 'Sun');
  const tz = weatherQuery.data?.timezone;

  const panels = (
    <Tabs defaultValue="location" className="flex h-full flex-col">
      <TabsList className="mx-3 mt-3 grid h-9 shrink-0 grid-cols-4">
        <TabsTrigger value="location" aria-label="Location" title="Location & geocodes">
          <MapPin className="size-4" />
        </TabsTrigger>
        <TabsTrigger value="sky" aria-label="Sky view" title="Sky view">
          <Telescope className="size-4" />
        </TabsTrigger>
        <TabsTrigger value="objects" aria-label="Objects" title="Bodies, satellites & aircraft">
          <Radar className="size-4" />
        </TabsTrigger>
        <TabsTrigger value="weather" aria-label="Weather" title="Weather">
          <CloudSun className="size-4" />
        </TabsTrigger>
      </TabsList>
      <div className="slim-scroll min-h-0 flex-1 overflow-y-auto">
        <TabsContent value="location" className="mt-0 p-3">
          <LocationPanel
            location={location}
            placeName={placeQuery.data}
            placeLoading={placeQuery.isLoading}
            timezone={tz}
          />
        </TabsContent>
        <TabsContent value="sky" className="mt-0 p-3">
          <SkyView
            location={location}
            bodies={bodies}
            satellites={satellites}
            selectedSat={selectedSat}
            onSelectSat={setSelectedSat}
          />
        </TabsContent>
        <TabsContent value="objects" className="mt-0 p-3">
          <ObjectsPanel
            location={location}
            bodies={bodies}
            satellites={satellites}
            satsLoading={tlesQuery.isLoading}
            satsError={tlesQuery.isError}
            onRetrySats={() => tlesQuery.refetch()}
            satsEnabled={layers.sats}
            aircraft={aircraft}
            aircraftTotal={aircraftQuery.data?.total ?? 0}
            aircraftLoading={aircraftQuery.isLoading}
            aircraftEnabled={layers.planes}
            timezone={tz}
            onFlyTo={flyToPoint}
            onSelectSat={setSelectedSat}
            onSelectPlane={setSelectedPlane}
          />
        </TabsContent>
        <TabsContent value="weather" className="mt-0 p-3">
          <WeatherPanel
            location={location}
            weather={weatherQuery.data}
            isLoading={weatherQuery.isLoading}
            isError={weatherQuery.isError}
            refetch={() => weatherQuery.refetch()}
          />
        </TabsContent>
      </div>
    </Tabs>
  );

  const vibedLink = (
    <div className="shrink-0 border-t p-2 text-center">
      <a
        href="https://marlowe.shakespeare.wtf"
        target="_blank"
        rel="noopener noreferrer"
        className="text-[11px] text-muted-foreground transition-colors hover:text-foreground"
      >
        Vibed with Marlowe
      </a>
    </div>
  );

  return (
    <div className="flex h-dvh flex-col overflow-hidden bg-background text-foreground">
      <header className="relative z-20 flex h-14 shrink-0 items-center gap-2 border-b bg-card/90 px-2.5 backdrop-blur sm:px-3">
        <img src="/favicon.svg" alt="" className="size-8 shrink-0" />
        <span className="hidden shrink-0 font-display text-lg font-bold tracking-tight sm:block">
          SkyScope
        </span>
        <div className="min-w-0 flex-1 sm:max-w-sm">
          <SearchBox
            onSelect={(p) => {
              setLocation({ lat: p.lat, lon: p.lon });
              flyToPoint(p, 9);
            }}
          />
        </div>
        <div className="ml-auto flex shrink-0 items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={locateMe}
            aria-label="Go to my location"
            title="Go to my location"
          >
            <LocateFixed className="size-4" />
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Map layers" title="Map layers">
                <Layers className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60">
              <DropdownMenuLabel>Map layers</DropdownMenuLabel>
              <DropdownMenuCheckboxItem
                checked={layers.planes}
                onCheckedChange={(c) => setLayers((l) => ({ ...l, planes: c === true }))}
              >
                <Plane className="mr-2 size-4" /> Aircraft (ADS-B)
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={layers.sats}
                onCheckedChange={(c) => setLayers((l) => ({ ...l, sats: c === true }))}
              >
                <Satellite className="mr-2 size-4" /> Satellites
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={layers.astro}
                onCheckedChange={(c) => setLayers((l) => ({ ...l, astro: c === true }))}
              >
                <Sun className="mr-2 size-4" /> Sun · Moon · Planet zeniths
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={layers.night}
                onCheckedChange={(c) => setLayers((l) => ({ ...l, night: c === true }))}
              >
                <Moon className="mr-2 size-4" /> Night shadow
              </DropdownMenuCheckboxItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel>Map style</DropdownMenuLabel>
              <DropdownMenuRadioGroup
                value={tileStyle}
                onValueChange={(v) => setTileStyle(v === 'osm' ? 'osm' : 'dark')}
              >
                <DropdownMenuRadioItem value="dark">Dark (OSM / CARTO)</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="osm">Standard (OSM)</DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
              <DropdownMenuSeparator />
              <DropdownMenuLabel>Aircraft altitude</DropdownMenuLabel>
              <div className="grid grid-cols-3 gap-x-2 gap-y-1 px-2 pb-1.5">
                {ALTITUDE_LEGEND.map(([color, label]) => (
                  <span
                    key={label}
                    className="inline-flex items-center gap-1.5 text-[10px] text-muted-foreground"
                  >
                    <span className="size-2 rounded-full" style={{ background: color }} />
                    {label}
                  </span>
                ))}
              </div>
            </DropdownMenuContent>
          </DropdownMenu>

          <ThemeToggle />

          <div className="hidden min-[500px]:block">
            <LoginArea className="max-w-36" />
          </div>

          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setPanelsOpen(true)}
            aria-label="Open panels"
            title="Open panels"
          >
            <PanelRightOpen className="size-4" />
          </Button>
        </div>
      </header>

      <div className="relative flex min-h-0 flex-1">
        <div className="relative isolate min-w-0 flex-1">
          <SkyMap
            initialCenter={{ lat: 30, lon: 10 }}
            initialZoom={3}
            location={location}
            onLocationChange={handleLocationChange}
            onViewChange={handleViewChange}
            flyTo={flyTo}
            tileStyle={tileStyle}
            layers={layers}
            aircraft={planesForMap}
            satellites={satellites}
            bodies={bodies}
            nightUrl={nightUrl}
            selectedSat={selectedSat}
            onSelectSat={setSelectedSat}
            selectedPlane={selectedPlane}
            onSelectPlane={setSelectedPlane}
          />

          {/* Status bar */}
          <div className="pointer-events-none absolute bottom-3 left-2 z-[1000] hidden sm:block">
            <div className="space-y-0.5 rounded-lg border bg-background/70 px-3 py-1.5 font-mono text-[11px] text-muted-foreground backdrop-blur">
              <div className="text-foreground">
                {formatDD(location)}{' '}
                <span className="text-muted-foreground">· {geohash(location, 7)}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center gap-1">
                  <Plane className="size-3" />
                  {aircraftQuery.data ? aircraftQuery.data.total : '—'}
                </span>
                <span className="inline-flex items-center gap-1">
                  <Satellite className="size-3" />
                  {satellites.length}
                </span>
                {sun && (
                  <span className="inline-flex items-center gap-1">
                    <Sun className="size-3" />
                    {sun.alt >= 0 ? '+' : ''}
                    {sun.alt.toFixed(0)}°
                  </span>
                )}
                {aircraftQuery.data && (
                  <span className="hidden md:inline">via {aircraftQuery.data.source}</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Desktop sidebar */}
        <aside className="hidden w-[380px] shrink-0 border-l bg-sidebar lg:flex lg:flex-col">
          <div className="min-h-0 flex-1">{panels}</div>
          {vibedLink}
        </aside>

        {/* Mobile sheet */}
        <Sheet open={panelsOpen} onOpenChange={setPanelsOpen}>
          <SheetContent side="right" className="flex w-full flex-col p-0 sm:max-w-md">
            <SheetHeader className="sr-only">
              <SheetTitle>SkyScope panels</SheetTitle>
            </SheetHeader>
            <div className="flex justify-center border-b p-2 min-[500px]:hidden">
              <LoginArea className="w-full max-w-64" />
            </div>
            <div className="min-h-0 flex-1">{panels}</div>
            {vibedLink}
          </SheetContent>
        </Sheet>
      </div>
    </div>
  );
};

export default Index;
